import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ActivityContext = createContext();

// Types d'activités
export const ACTIVITY_TYPES = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  IMPORT: 'import',
  EXPORT: 'export',
  LOGIN: 'login',
  ERROR: 'error',
};

// Icônes et couleurs par type
export const ACTIVITY_STYLES = {
  create: { color: 'text-green-500', bg: 'bg-green-500/10', icon: '✓' },
  update: { color: 'text-blue-500', bg: 'bg-blue-500/10', icon: '↻' },
  delete: { color: 'text-red-500', bg: 'bg-red-500/10', icon: '✕' },
  import: { color: 'text-purple-500', bg: 'bg-purple-500/10', icon: '↓' },
  export: { color: 'text-orange-500', bg: 'bg-orange-500/10', icon: '↑' },
  login: { color: 'text-gray-500', bg: 'bg-gray-500/10', icon: '→' },
  error: { color: 'text-red-600', bg: 'bg-red-600/10', icon: '!' },
};

const MAX_ACTIVITIES = 50;
const STORAGE_KEY = 'bureau_agricole_activities';

export function ActivityProvider({ children }) {
  const [activities, setActivities] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Charger les activités depuis localStorage au démarrage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setActivities(parsed.slice(0, MAX_ACTIVITIES));
        // Compter les non lues (moins de 5 minutes)
        const fiveMinAgo = Date.now() - 5 * 60 * 1000;
        const unread = parsed.filter(a => new Date(a.timestamp).getTime() > fiveMinAgo).length;
        setUnreadCount(unread);
      }
    } catch (e) {
      console.error('Erreur chargement activités:', e);
    }
    
    // Écouter les événements d'activité depuis l'API
    const handleActivityAdded = (event) => {
      const newActivity = event.detail;
      setActivities(prev => {
        // Éviter les doublons
        if (prev.some(a => a.id === newActivity.id)) return prev;
        return [newActivity, ...prev].slice(0, MAX_ACTIVITIES);
      });
      setUnreadCount(prev => prev + 1);
    };
    
    window.addEventListener('activity-added', handleActivityAdded);
    return () => window.removeEventListener('activity-added', handleActivityAdded);
  }, []);

  // Sauvegarder dans localStorage
  useEffect(() => {
    if (activities.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(activities.slice(0, MAX_ACTIVITIES)));
    }
  }, [activities]);

  // Ajouter une nouvelle activité
  const addActivity = useCallback((type, title, details = '', module = '') => {
    const newActivity = {
      id: `act-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      title,
      details,
      module,
      timestamp: new Date().toISOString(),
      read: false,
    };

    setActivities(prev => [newActivity, ...prev].slice(0, MAX_ACTIVITIES));
    setUnreadCount(prev => prev + 1);

    return newActivity;
  }, []);

  // Marquer une activité comme lue
  const markAsRead = useCallback((activityId) => {
    setActivities(prev => prev.map(a => 
      a.id === activityId ? { ...a, read: true } : a
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Marquer toutes comme lues
  const markAllAsRead = useCallback(() => {
    setActivities(prev => prev.map(a => ({ ...a, read: true })));
    setUnreadCount(0);
  }, []);

  // Effacer l'historique
  const clearHistory = useCallback(() => {
    setActivities([]);
    setUnreadCount(0);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Ouvrir/fermer le panneau
  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  // Helper pour enregistrer facilement les actions
  const logCreate = useCallback((title, details, module) => 
    addActivity(ACTIVITY_TYPES.CREATE, title, details, module), [addActivity]);
  
  const logUpdate = useCallback((title, details, module) => 
    addActivity(ACTIVITY_TYPES.UPDATE, title, details, module), [addActivity]);
  
  const logDelete = useCallback((title, details, module) => 
    addActivity(ACTIVITY_TYPES.DELETE, title, details, module), [addActivity]);
  
  const logImport = useCallback((title, details, module) => 
    addActivity(ACTIVITY_TYPES.IMPORT, title, details, module), [addActivity]);
  
  const logExport = useCallback((title, details, module) => 
    addActivity(ACTIVITY_TYPES.EXPORT, title, details, module), [addActivity]);
  
  const logError = useCallback((title, details, module) => 
    addActivity(ACTIVITY_TYPES.ERROR, title, details, module), [addActivity]);

  const value = {
    activities,
    unreadCount,
    isPanelOpen,
    addActivity,
    markAsRead,
    markAllAsRead,
    clearHistory,
    togglePanel,
    openPanel,
    closePanel,
    // Helpers
    logCreate,
    logUpdate,
    logDelete,
    logImport,
    logExport,
    logError,
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
}

export function useActivity() {
  const context = useContext(ActivityContext);
  if (!context) {
    throw new Error('useActivity must be used within an ActivityProvider');
  }
  return context;
}

export default ActivityContext;
